export default {
	account: {
		title: "Account settings",
		sections: {
			profile: "Profile",
			invitations: "Invitations",
			security: "Security",
			privacy: "Privacy & storage",
		},
		profile: {
			color: "Colour",
			managedByProvider:
				"Your name and email are managed in your account. Only language and colour can be changed here.",
			title: "Profile",
			emailConfirmed: "Email confirmed",
			emailNotConfirmed: "Email not confirmed",
			save: "Save changes",
			updated: "Profile updated.",
			failed: "The profile could not be updated.",
		},
		security: {
			title: "Sign-in and security",
			description:
				"Manage your password, two-factor authentication and passkeys in your account.",
			manage: "Manage security settings",
		},
		pats: {
			title: "Personal access tokens",
			description:
				"Personal access tokens give scripts and CI jobs unattended access to Notitia. " +
				"Each token is limited to the permissions you choose on specific projects or " +
				"dataspaces, and never grants server-wide access.",
			empty: "You have not created any personal access tokens yet.",
			actions: {
				create: "Create token",
			},
			revoke: {
				title: "Revoke this token?",
				body: "Anything using the token \u201C{name}\u201D loses access immediately. This cannot be undone.",
			},
			create: {
				title: "Create personal access token",
			},
			secret: {
				usage:
					"To use it, exchange it at this server for an access token, as shown below. " +
					"Send that access token as the bearer token in your API calls. The access " +
					"token expires after a few minutes; exchange again then.",
			},
			revoked: "Token revoked.",
			error: {
				pat_lifetime_exceeded: "A token can be valid for at most 90 days.",
				pat_name_invalid: "The name must be 1–100 characters.",
				pat_grants_invalid: "Choose between 1 and 100 permissions.",
				pat_grant_not_held:
					"You no longer hold one of the selected permissions. Reload the list and choose again.",
				pat_mint_refused:
					"The account service refused to issue the token. Try again shortly.",
				idp_unavailable: "The account service is currently unreachable. Try again shortly.",
				pat_not_allowed: "Personal access tokens cannot be managed with a token.",
				missing_entitlement: "Your role does not allow tokens that never expire.",
				pat_mint_mismatch:
					"The account service issued the token with a different expiry than requested, " +
					"so it was revoked straight away. Report this to an administrator.",
				generic: "Something went wrong. Try again.",
			},
		},
		privacy: {
			title: "Local data",
			description:
				"What Notitia keeps on this device, read from the same table the app writes " +
				"through — so this list cannot drift from what is actually stored.",
			notice: {
				noTracking:
					"No tracking cookies, no third-party tracking, no analytics. Notitia sets " +
					"only the cookies signing in needs (listed below), loads no analytics or " +
					"error-reporting scripts, and embeds nothing from an advertising or social " +
					"network.",
				externalHosts:
					"Outside this installation the app reaches two hosts. auth.livereader.com is " +
					"LiveReader's account service: your browser is sent there to sign in and out " +
					"(with silent sign-in on, on every visit without a session), and the Notitia " +
					"server asks it for your profile. What the account service stores is listed " +
					"below under “At the account service”. cdn.graphly.dev serves the diagram " +
					"shape templates a project chooses to use, and runs on the same " +
					"infrastructure as the rest of Notitia. LiveReader runs both itself, so " +
					"neither is a transfer to a third party.",
				scope: "Beyond that, what Notitia keeps on this device is what the tables below show.",
				legal: "Company information, imprint and privacy policy are published at",
			},
			table: {
				key: "Key",
				purpose: "Category",
				what: "What it is for",
				size: "Size",
			},
			purposes: {
				essential: "Essential",
				preference: "Preference",
				viewState: "View state",
			},
			scopes: "{count} entry | {count} entries",
			entries: {
				storageVersion:
					"Records which key scheme this device is on, so the one-off rename of the " +
					"older keys runs exactly once.",
				pendingInvitation:
					"The invitation you chose “Sign in to accept” on, so the next sign-in on " +
					"this device opens it, even if you create an account first. Removed when it opens, " +
					"and ignored after a day.",
				themeMode: "Light, dark, or following your system.",
				locale: "The language you picked.",
				logHeight: "Height of the process log bar.",
				operatorPanelSplits:
					"How a process view's columns and rows are divided, as you dragged them, per kind of process.",
				envNameColWidth: "Width of the name column in a dataspace's environment variables.",
				processOverviewWidth: "Width of the process editor's overview panel.",
				processPropertiesWidth: "Width of the process editor's properties panel.",
				actionPaletteWidth: "Width of the activity editor's action palette.",
				actionPropertiesWidth: "Width of the activity editor's properties panel.",
				dataobjectListWidth: "Width of the model editor's dataobject list.",
				dataobjectPropertiesWidth: "Width of the model editor's properties panel.",
				typePropertiesWidth: "Width of the types page's properties panel.",
				processSidebarSide: "Which edge the process editor docks its panels to.",
				actionSidebarSide: "Which edge the activity editor docks its panels to.",
				dataobjectSidebarSide: "Which edge the model editor docks its panels to.",
				sidebarAutoCollapse: "Whether opening a panel collapses the ones behind it.",
				processOverviewCollapsed:
					"Whether you collapsed the process editor's overview panel.",
				actionPaletteCollapsed:
					"Whether you collapsed the activity editor's action palette.",
				dataobjectListCollapsed:
					"Whether you collapsed the model editor's dataobject list.",
				processSidebarSort: "How the process editor sorts and filters its dataobject list.",
				processAvailableSort:
					"How the process editor sorts the dataobjects available to add.",
				projectSearch: "Your search on the project list.",
				dataspaceSearch: "Your search on the dataspace list.",
				processListSearch: "Your search on a project's process list, per project.",
				activityListSearch: "Your search on a project's activity list, per project.",
				typeListSearch: "Your search on a project's datatype list, per project.",
				terminologySearch: "Your search on a project's terminology list, per project.",
				dataobjectViewsSearch: "Your search on a project's view list, per project.",
				dataobjectListSearch: "Your search on a project's dataobject table, per project.",
				processDataobjectSearch:
					"Your search in the process editor's dataobject list, per process.",
				modelSidebarSearch: "Your search in the model editor's dataobject list, per view.",
				processSidebarTab: "Which tab the process editor's sidebar was on, per process.",
				projectHomeTab: "Whether you last had Overview or Settings open, per project.",
				projectHomeSection: "Which settings section you last had open, per project.",
				dataobjectsTab: "Whether you last had Views or Dataobjects open, per project.",
				dataobjectListRows:
					"Whether the dataobject table last listed dataobjects or their attributes, per project.",
				operatorPanelTabs:
					"Which panel each place of a process view showed, where panels share a place as tabs, per process.",
				viewListSort: "How you sort a project's view list.",
				processListSort: "How you sort a project's process list.",
				activityListSort: "How you sort a project's activity list.",
				activityListGrouping: "How you group a project's activity list, per project.",
				activityListFolded:
					"Which sections of a project's activity list you folded, per project.",
				typeListSort: "How you sort a project's datatype list.",
				dataobjectListSort: "How you sort a project's dataobject table.",
				terminologySort: "How you sort a project's terminology list.",
				recentViews:
					"The views you opened last, per project, for sorting by recently opened.",
				recentProcesses:
					"The processes you opened last, per project, for sorting by recently opened.",
				recentActivities:
					"The activities you opened last, per project, for sorting by recently opened.",
				recentTypes:
					"The datatypes you opened last, per project, for sorting by recently opened.",
				recentDataobjects:
					"The dataobjects you opened last, per project, for sorting by recently opened.",
			},
			actions: {
				clearViewState: "Clear view state",
				clearViewStateHint: "Searches, filters, sort and open tabs.",
				clearPreferences: "Clear preferences",
				clearPreferencesHint:
					"Theme, language and panel layout. The page reloads so the interface " +
					"goes back to its defaults.",
			},
			cleared: {
				viewState: "Searches, filters and tab state cleared.",
				app: "Offline support removed from this device.",
			},
			signOutNote:
				"Signing out clears your session and your view state automatically. " +
				"Preferences stay, so the app looks the way you left it next time.",
			cookies: {
				title: "Cookies",
				description:
					"Set by the Notitia server, for signing in only. They are HttpOnly, so no " +
					"script can read them, Notitia's included, and they are sent only to " +
					"Notitia's sign-in addresses.",
				table: {
					name: "Name",
					what: "What it is for",
					lifetime: "Lifetime",
				},
				entries: {
					session: {
						what:
							"A random id for your session and nothing else. Everything else stays " +
							"on the server.",
						lifetime:
							"Until you sign out. The session ends after 8 hours unused, after 12 " +
							"hours at most, and when the server restarts.",
					},
					login: {
						what:
							"Ties a sign-in you started to this browser, so nobody else can " +
							"complete it. One per sign-in in progress, so tabs signing in at once " +
							"do not replace each other's.",
						lifetime: "10 minutes at most. Cleared as soon as the sign-in finishes.",
					},
				},
			},
			app: {
				title: "Installed app and offline page",
				description:
					"Once you are signed in, your browser sets up a small helper for Notitia, a " +
					"service worker. It shows an offline page when the server cannot be reached, " +
					"and tells you when a new version is available. It does not read or keep " +
					"anything you do in the app, and it never answers a request from a copy of " +
					"its own. Installing Notitia as an app is done in your browser's menu and " +
					"stores nothing more.",
				entries: {
					worker: {
						what:
							"Shows the offline page when the server cannot be reached, and notices " +
							"new versions. It acts only on Notitia's own pages.",
						lifetime:
							"Until you remove it here or clear this site's data in your browser. " +
							"It is set up again the next time you open Notitia signed in.",
					},
					cache: {
						what:
							"The offline page with its script, style and logo: the same files for " +
							"everyone, and nothing about you. Named for the version it belongs to.",
						lifetime: "Replaced with each new version, and removed with the helper.",
					},
				},
				clear: "Remove offline support",
				clearHint:
					"Removes the helper and its cache from this device. It comes back the next " +
					"time you open Notitia signed in.",
			},
			stored: {
				title: "Stored on servers",
				server: {
					title: "On the Notitia server",
					intro:
						"Notitia stores no passwords, two-factor secrets or passkeys: the account " +
						"service handles signing in. This server holds:",
					items: {
						profile:
							"Your profile as the account service reports it when you sign in: id, " +
							"email address and whether it is confirmed, name. Plus your language, " +
							"your colour and your system role.",
						memberships:
							"Your memberships and roles in projects and dataspaces, invitations you " +
							"sent or accepted, and what you created.",
						tokens:
							"For each personal access token, its name, permissions, and creation, " +
							"expiry and revocation dates. The token itself is not stored.",
						session:
							"Your session, in memory only: the account service's tokens for your " +
							"sign-in. It is gone when you sign out, when it expires, and when the " +
							"server restarts.",
					},
				},
				provider: {
					title: "At the account service",
					intro:
						"You sign in through LiveReader's account service, a separate service with " +
						"its own storage. You manage what it holds in your account there, not " +
						"here. It stores:",
					items: {
						account:
							"Your account: sign-in name (your email address), name, language, and " +
							"your password as an Argon2id hash.",
						factors:
							"Your second factors: the TOTP secret, the recovery codes as hashes, " +
							"and your passkeys.",
						sessions:
							"Your sign-in sessions and devices, each with IP address and browser " +
							"identifier (user agent). Expired sessions are deleted hourly.",
						audit:
							"A security log of your sign-ins and account changes with IP address " +
							"and browser identifier, and for a failed sign-in the name that was " +
							"typed. No deletion period is set for this log at present.",
						consents:
							"Which applications you allowed which access, and your personal access " +
							"tokens at the account service (the secret only as an HMAC).",
						shared:
							"When you sign in, it passes your id, your email address and whether it " +
							"is confirmed, your name and your Notitia system role to Notitia, and a " +
							"logout notice when you sign out.",
						cookies:
							"In your browser it sets cookies of its own, on its own address: the " +
							"sign-in session (until the browser closes, or up to 30 days with “keep " +
							"me signed in”), a device id (400 days), cross-site request protection " +
							"(CSRF), an id for a sign-in in progress (1 hour), and a refresh token " +
							"for its own account portal.",
						mail:
							"It sends email, for example to confirm your address, through a mail " +
							"server, and by default deletes delivered messages after 7 days. It uses " +
							"no analytics, captchas or other third-party services.",
					},
				},
			},
			elsewhere: {
				title: "Held elsewhere, and not clearable from here",
				passkey:
					"A passkey is stored by your operating system or security key, never by " +
					"Notitia. You register and remove passkeys in your security settings at " +
					"the account service; deleting one from the device is done in your " +
					"system's password settings.",
				cache:
					"Your browser caches this app's scripts, styles and fonts. Clearing the " +
					"browser cache removes those.",
			},
		},
	},
};
